import React, { useEffect, useState } from "react";
import { OsTypes, osName } from "react-device-detect";
import { useAuthState } from "react-firebase-hooks/auth";

import { displayCityName, displayCountryName } from "components/b2c/explore/exploreCommon";
import useUser from "components/common/useUser";
import { useBusinessContext, useOrganisation } from "context/BusinessContext";
import "tippy.js/animations/scale.css";
import "tippy.js/dist/tippy.css";
import useReactRouter from "use-react-router";
import firebase from "../../firebase";
import "../css/Website.scss";
import { useAppDownloadUrl } from "components/common/useAppStoreLink";
//@ts-ignore
import Icon from "components/Icons/Icon";
import trackEvent from "components/tracking/trackEvent";
import trackFathom from "components/tracking/trackFathom";
import { discoverPath } from "i18n/urlLocale";
import { useMarketingLang } from "i18n/useMarketingLang";
import BusinessWaveBanner from "../components/BusinessWaveBanner";
import ClosingCTASection from "../components/ClosingCTASection";
import CommunitySection from "../components/CommunitySection";
import DownloadAppQrCard from "../components/DownloadAppQrCard";
import FeatureToolboxSection from "../components/FeatureToolboxSection";
import Footer from "../components/Footer";
import HeroSearchCard, { type HeroSearchTab } from "../components/HeroSearchCard";
import HeroShowcase from "../components/HeroShowcase";
import LanguageSwitcher from "../components/LanguageSwitcher";
import LatestArticlesSection from "../components/LatestArticlesSection";
import Navigation from "../components/navigation/Navigation";
import TestimonialsSection from "../components/TestimonialsSection";
import TrendingDestinationsSection from "../components/TrendingDestinationsSection";

const StipplBusinessLanding = React.lazy(() => import("../content/business/homepage/BusinessHomePage"));

// Decorative hero social-proof avatars — placeholder stock photos, not real users.
const HERO_AVATARS = [
	"1548213238-0da7521bd6e0",
	"1637692513738-68ed61f6604a",
	"1612000649051-611809293050",
	"1742783106217-7204748c6874",
	"1578955682343-97ea7f7464da",
];

/**
 * Sitewide links into the /discover guides. Every entry must be a geo that has passed the
 * knowledge base's readiness check — i.e. one whose Explore document carries a `guide` field.
 *
 * This list used to name eight countries of which only Japan and Thailand were ready; the other
 * six pointed at pages built from a tagline and a climate table, from every page on the site.
 * Those are `noindex` now (the X-Robots-Tag gate in server.ts), so it was no longer an SEO
 * problem — but it still sent anyone clicking "Greece" to a near-empty page.
 *
 * Deliberately hardcoded rather than queried: this list renders on every marketing page including
 * server-rendered ones (via Footer), and the SSR Apollo client resolves nothing, so a query here
 * would render an empty column on the server and a full one after hydration.
 *
 * To refresh, take the ready set from the knowledge base:
 *   python3 tools/check_readiness.py | grep '✓'
 * Only two countries qualify today, so the rest of the list is ready destinations — which are
 * also the deeper pages that benefit most from a sitewide link.
 */
export const POPULAR_DESTINATIONS: { label: string; geo: string; flag: string }[] = [
	{ label: "Japan", geo: "japan", flag: "jp" },
	{ label: "Tokyo", geo: "japan/tokyo", flag: "jp" },
	{ label: "Kyoto", geo: "japan/kyoto", flag: "jp" },
	{ label: "Osaka", geo: "japan/osaka", flag: "jp" },
	{ label: "Thailand", geo: "thailand", flag: "th" },
	{ label: "Bangkok", geo: "thailand/bangkok", flag: "th" },
	{ label: "Chiang Mai", geo: "thailand/chiang-mai", flag: "th" },
	{ label: "Phuket", geo: "thailand/phuket", flag: "th" },
];

/**
 * 123yb69329x: `label` above is the English canonical name — kept that way because it also
 * keys `flag`/`geo` and other call sites may match on it — so every renderer of
 * `POPULAR_DESTINATIONS` has to localise at the point it prints the label, not read it as-is.
 * `geo` is what tells a country entry ("japan") from a destination entry ("japan/tokyo") apart:
 * one path segment vs two. Countries go through `displayCountryName` (Intl region data, covers
 * every country); destinations go through `displayCityName` (the curated `CITY_EXONYMS` map) —
 * both already used by the /discover pages, and both no-op for English or an unresolved name,
 * so an entry this doesn't recognise just keeps rendering its English label.
 */
export const localizedDestinationLabel = ({ label, geo }: { label: string; geo: string }, lang: string): string =>
	geo.includes("/") ? displayCityName(label, lang) : displayCountryName(label, lang);

const Home = () => {
	const [user] = useAuthState(firebase.auth);
	const { user: stipplUser, initializing } = useUser();
	const { history } = useReactRouter();
	const { isBusinessMode: isBusiness } = useBusinessContext();
	const [heroSearchTab, setHeroSearchTab] = useState<HeroSearchTab>("plan");
	const lang = useMarketingLang();

	// Query organisation data in business mode
	const { organisation, loading: orgLoading } = useOrganisation();

	useEffect(() => {
		// Wait for queries to complete
		if (!user || !stipplUser?.createdAt) {
			return;
		}

		// In business mode, wait for org query and redirect to org page
		if (isBusiness) {
			if (orgLoading) return; // Still loading

			const orgName = organisation?.name;
			if (orgName) {
				history.push(`/${encodeURIComponent(orgName)}`);
			}
			// If no org name, stay on homepage - don't redirect to onboarding
			return;
		}

		// In consumer mode, redirect to home or onboarding
		const hasValidUsername = !!stipplUser?.username && !stipplUser?.username?.includes("StipplUser");
		if (hasValidUsername) {
			history.push("/home");
		} else {
			history.push("/onboarding");
		}
	}, [user, initializing, stipplUser, isBusiness, organisation, orgLoading]);

	const handleSignupTracking = (location) => {
		if (osName === OsTypes.Android) {
			trackFathom("BNQKH0MH");
			trackEvent("click_googleplay_cta", { path: location }, "marketing_site");
		} else if (osName === OsTypes.IOS) {
			trackFathom("FD21GVOP");
			trackEvent("click_appstore_cta", { path: location }, "marketing_site");
		} else {
			trackEvent("click_signup_cta", { path: location }, "marketing_site");
			trackFathom("E4NT4HWN");
		}
	};

	// 123yb6937h3: always the attributed OneLink now, mobile or desktop -- see the hook's doc
	// comment in useAppStoreLink.ts.
	const appstoreUrl = useAppDownloadUrl();

	// Show B2B landing page when in business mode
	if (isBusiness) {
		return <StipplBusinessLanding />;
	}

	return (
		<div id="stippl-homepage">
			<Navigation impact={true} langMenu={<LanguageSwitcher type="page" />} />
			<section className="intro-header">
				<div className="intro-header__grid">
					<div className="intro-header__content">
						<a
							href={appstoreUrl}
							className="intro-header__get-app"
							onClick={() => handleSignupTracking("homepage_get_app_pill")}
							target="_blank"
							rel="noreferrer"
						>
							<span className="intro-header__get-app-icon">
								<Icon name="stippl" size={15} fill="white" />
							</span>
							Get the app
						</a>

						<div className="intro-header__social-proof">
							<div className="intro-header__avatars">
								{HERO_AVATARS.map((id) => (
									<img key={id} src={`https://images.unsplash.com/photo-${id}?crop=faces&fit=crop&fm=jpg&q=80&w=80&h=80`} alt="" loading="lazy" />
								))}
							</div>
							<div>
								<div className="intro-header__stars">
									{[0, 1, 2, 3, 4].map((i) => (
										<Icon key={i} name="star--filled" size={12} fill="#f5a623" />
									))}
								</div>
								<span>
									Joined by <strong>1.5M+ travelers</strong>
								</span>
							</div>
						</div>

						<span className="intro-header__eyebrow">
							<Icon name="sparkle-ai" size={13} />
							Meet your AI travel planner
						</span>

						<h1>
							Where to next?
							<br />
							Let&apos;s <span className="intro-header__accent">plan</span> it.
						</h1>

						<h2 className="intro-p">
							Stippl is your free AI travel planner. <br className="intro-p__br" />
							Describe your dream trip and the AI builds a full day-by-day itinerary: budget, stays and bookings, all in one app.
						</h2>

						<HeroSearchCard onTabChange={setHeroSearchTab} />

						{heroSearchTab !== "explore" && (
							<div className="intro-header__popular">
								<span>Popular:</span>
								{POPULAR_DESTINATIONS.map(({ label, geo }) => (
									<a href={discoverPath(`/discover/${geo}`, lang)} key={geo}>
										{localizedDestinationLabel({ label, geo }, lang)}
									</a>
								))}
							</div>
						)}
					</div>

					<HeroShowcase />
				</div>
			</section>

			<TrendingDestinationsSection />

			<FeatureToolboxSection />

			<CommunitySection />

			<TestimonialsSection variant="homepage" />

			<BusinessWaveBanner />

			<LatestArticlesSection />

			<ClosingCTASection />

			<Footer />
			<DownloadAppQrCard />
		</div>
	);
};

export default Home;
